import { CompactInlineAlertWidget,
  InlineAlertType,
  TabStyles,
  Tooltip, TooltipPosition
} from '@tableau/widgets';
import {AxiosError} from 'axios';
import {useField, useFormikContext} from 'formik';
import React, {ReactElement, useEffect, useState} from 'react';
import {style} from 'typestyle';

import {FormikCheckbox} from '~commonInfra/components/FormikCheckbox';
import {FeatureFlags} from '~commonInfra/featureFlags/FeatureFlags';
import {CommonStyles} from '~commonInfra/styles/CommonStyles';
import { SiteSettingsErrorCode} from '~commonInfra/utils/ErrorCodes';
import {imgPath} from '~commonInfra/utils/ImgUrl';
import {ExtractEncryptionMode} from '~commonInfra/utils/sites/SitePropertyTypes';
import {SessionServerSelectors, SessionSiteSelectors} from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import {Messages} from '~src/codegen/Localize';
import {ResultHelper} from '~src/httpClient/utils/ResultHelper';
import {
  siteEncryptionKeyMetadata, siteEncryptionKeyMetadataEntry
} from '~src/kmsInOnline/assets/KMSInOnlineSettingsStyles';
import {DownloadKeyHistoryLogButton} from '~src/kmsInOnline/components/buttons/DownloadKeyHistoryLogButton';
import {GenerateSiteEncryptionKeyButton} from '~src/kmsInOnline/components/buttons/GenerateSiteEncryptionKeyButton';
import {encryptionConfirmationDialog} from '~src/kmsInOnline/components/dialogs/SiteEncryptionEnablementDialog';
import {KeyOperationsMenu} from '~src/kmsInOnline/components/dropdowns/KeyOperationsMenu';
import {IGetSiteEncryptionKeyResult} from '~src/kmsInOnline/serverApi/GetSiteEncryptionKeyRequest';
import {
  RetrieveSiteEncryptionKeyService
} from '~src/kmsInOnline/services/RetrieveSiteEncryptionKeyService';
import {SettingsSection} from '~src/settings/components/common/SettingsSection';
import {componentRegistryEntry} from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import {SiteSettingsForSiteAdminProps} from '~src/settings/general-settings/components/site-settings/ReactSiteSettingsForSiteAdmin';
import {useSelector} from '~src/settings/general-settings/utils';
import hasErrors = ResultHelper.hasErrors;

export type KMSInOnlineProps = SiteSettingsForSiteAdminProps<'extractEncryptionMode' | 'cmekAvailable'>;

export const KMS_IN_ONLINE_SETTINGS_ENCRYPTION_ENABLE_CHECKBOX = 'kms-tol-generate-key-checkbox';
export const KMS_IN_ONLINE_SITE_SETTINGS_TEST_ID = 'kms-tol-site-settings-test-id';
export const KMS_IN_ONLINE_ENABLEMENT_INFO_LINK = 'kms-tol-enablement-information-link';
export const KMS_IN_ONLINE_SITE_ENCRYPTION_KEY_METADATA = 'site-encryption-key-metadata';
export const KMS_IN_ONLINE_ENABLED_TOOLTIP = 'site-encryption-enabled-tooltip';
export const KMS_IN_ONLINE_SERVER_ALERT = 'kms-in-online-alert';

export const KMS_EXTRACT_ENCRYPTION_MODE = 'settings.extractEncryptionMode';

/**
 * Settings section for management of Customer Managed Encryption Keys (CMEK) feature in TOL
 * @param props
 */
export function KMSInOnlineSection(props: KMSInOnlineProps): JSX.Element {
  const [field] = useField({name: KMS_EXTRACT_ENCRYPTION_MODE});
  const [siteEncryptionKey, setSiteEncryptionKey] = useState<IGetSiteEncryptionKeyResult>(undefined);
  const formik = useFormikContext<KMSInOnlineProps>();

  // Check if key exists and retrieve it if it does
  useEffect(() => {
    let isMounted = true;
    async function retrieveExtractKeyValue() {
      if (formik.initialValues.settings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED) {
        let response = await retrieveExtractKey();
        if (isMounted) {
          setSiteEncryptionKey({...response});
        }
      }
    }

    retrieveExtractKeyValue().catch();
    return () => { isMounted = false; };
  }, [formik.initialValues.settings.extractEncryptionMode]);

  const handleCheckboxChange = async (): Promise<void> => {
    const newValue = field.value === ExtractEncryptionMode.DISABLED
      ? ExtractEncryptionMode.ENFORCED
      : ExtractEncryptionMode.DISABLED;
    if (formik.getFieldMeta(KMS_EXTRACT_ENCRYPTION_MODE).value === ExtractEncryptionMode.DISABLED) {
      encryptionConfirmationDialog().then(() => {
        formik.setFieldValue(KMS_EXTRACT_ENCRYPTION_MODE, newValue);
      });
    } else {
      formik.setFieldValue(KMS_EXTRACT_ENCRYPTION_MODE, newValue);
    }
  };

  function CheckboxLabel(): JSX.Element {
    return siteEncryptionKey === ExtractEncryptionMode.ENFORCED
      ? kmsInOnlineTooltip() : <div>{Messages.extractEncryptionTOLCheckboxLabel()}</div>;
  }

  return useShowSettingsSection(props) ?
    <SettingsSection
      title={Messages.kmsInOnlineTitle()}
      description={Messages.extractEncryptionTOLDescription()}
      testId={KMS_IN_ONLINE_SITE_SETTINGS_TEST_ID}
    >
      <FormikCheckbox
        style={{marginLeft: 0}}
        label={''}
        name={KMS_EXTRACT_ENCRYPTION_MODE}
        testId={KMS_IN_ONLINE_SETTINGS_ENCRYPTION_ENABLE_CHECKBOX}
        onChange={handleCheckboxChange}
        disabled={formik.initialValues.settings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED}
        checked={formik.getFieldMeta(KMS_EXTRACT_ENCRYPTION_MODE).value === ExtractEncryptionMode.ENFORCED}
        childrenSkipIndent={true}
        checkboxWithLabelChildren={CheckboxLabel()}
      />
      {getFormBody(siteEncryptionKey, setSiteEncryptionKey)}
    </SettingsSection> : null;
}

// TODO check props formatting?
function getFormBody(siteEncryptionKey: IGetSiteEncryptionKeyResult, setSiteEncryptionKey: (s: IGetSiteEncryptionKeyResult) => void): JSX.Element {
  return <div>
      <div className={siteEncryptionKeyMetadata} id={'keyOperationsBlock'}>
        {RenderSiteEncryptionKeyMetadata(siteEncryptionKey)}
        {DownloadKeyHistoryLogButton(siteEncryptionKey)}
      </div>
      <div style={{ display: 'inline-block', verticalAlign: 'top', width: '25%'}}>
        {GenerateSiteEncryptionKeyButton(siteEncryptionKey, setSiteEncryptionKey)}
        {KeyOperationsMenu(siteEncryptionKey, setSiteEncryptionKey)}
      </div>
      {RenderKMSServerErrors(siteEncryptionKey)}
    </div>;
}

export function keyNotFoundError(siteEncryptionKey: IGetSiteEncryptionKeyResult) {
  return hasErrors(siteEncryptionKey) && siteEncryptionKey?.errors[0].code === SiteSettingsErrorCode.SiteEncryptionKeyNotFound;
}

/**
 * Retrieve the current extraction key if it exists, return error if it cannot be retrieved
 */
export function retrieveExtractKey(): Promise<IGetSiteEncryptionKeyResult> {
  const callbackWithResult = (result: IGetSiteEncryptionKeyResult) => {
    return result;
  };

  const callbackWithError = (result: AxiosError) => {
    return {errors: [{code: result.response.status}]};
  };
  return RetrieveSiteEncryptionKeyService.retrieveSiteEncryptionKey()
    .then(callbackWithResult, callbackWithError);
}

/**
 * Display server errors as warning or error in settings section
 * @param siteEncryptionKey
 */
function RenderKMSServerErrors(siteEncryptionKey: IGetSiteEncryptionKeyResult): JSX.Element {
  let message: string, alertType: InlineAlertType;
  let code: number = hasErrors(siteEncryptionKey) ? siteEncryptionKey.errors[0].code : -1;
  switch (code) {
    case SiteSettingsErrorCode.SiteEncryptionKeyNotFound:
      message = Messages.extractEncryptionTOLKeyDeletedAlert();
      alertType = InlineAlertType.Warning;
      break;
    default:
      message = Messages.extractEncryptionTOLKeyGenericAlert();
      alertType = InlineAlertType.Error;
  }

  return hasErrors(siteEncryptionKey) ? <div style={{whiteSpace: 'nowrap', display: 'inline-block', marginTop: CommonStyles.Sizes.SmallSpace}}>
    <CompactInlineAlertWidget testId={KMS_IN_ONLINE_SERVER_ALERT} type={alertType} children={message}/>
  </div> : null;
}

function kmsInOnlineTooltip(): JSX.Element {
  return <div><Tooltip
    testId={KMS_IN_ONLINE_ENABLED_TOOLTIP}
    getContent={() => {return <span>{Messages.extractEncryptionTOLEnabledTooltip()}</span>;}}
  >
    <span>{Messages.extractEncryptionTOLCheckboxLabel()}
    <img className={style({ paddingLeft: TabStyles.Spacing.SmallSpace})}
         data-tb-test-id={KMS_IN_ONLINE_ENABLED_TOOLTIP}
         src={imgPath('data_prep_info.svg')}
         style={{verticalAlign: 'middle'}}
         alt={Messages.extractEncryptionTOLEnabledTooltip()} />
    </span>
  </Tooltip></div>;
}

/**
 * Render metadata for site encryption key - UID, VersionID, dateCreated and dateRotated
 */
function RenderSiteEncryptionKeyMetadata(siteEncryptionKey: IGetSiteEncryptionKeyResult): ReactElement {
  if (keyNotFoundError(siteEncryptionKey)) {
    return <div title={'siteEncryptionKeyMetadata'} data-tb-test-id={KMS_IN_ONLINE_SITE_ENCRYPTION_KEY_METADATA} className={siteEncryptionKeyMetadata}>
      <div className={siteEncryptionKeyMetadataEntry}>
        {Messages.extractEncryptionTOLKey({KEY_ID: Messages.extractEncryptionTOLKeyEmpty()})}
      </div>
    </div>;
  } else if (siteEncryptionKey !== undefined || !hasErrors(siteEncryptionKey)) {
    return <div title={'siteEncryptionKeyMetadata'} data-tb-test-id={KMS_IN_ONLINE_SITE_ENCRYPTION_KEY_METADATA} className={siteEncryptionKeyMetadata}>
      {siteEncryptionKey?.keyId && <Tooltip maxWidth={1000} position={TooltipPosition.Top} getContent=
        {() => <span>{Messages.extractEncryptionTOLKey({KEY_ID: siteEncryptionKey.keyId})}</span>}>
        <div className={siteEncryptionKeyMetadataEntry}>
          {Messages.extractEncryptionTOLKey({KEY_ID: siteEncryptionKey?.keyId.substring(0, 16) + '...'})}
        </div>
      </Tooltip>}
      {siteEncryptionKey?.keyVersionId && <Tooltip maxWidth={1000} position={TooltipPosition.Top} getContent=
        {() => <span>{Messages.extractEncryptionTOLKeyVersion({KEY_VERSION_ID: siteEncryptionKey.keyVersionId})}</span>}>
        <div className={siteEncryptionKeyMetadataEntry}>
          {Messages.extractEncryptionTOLKeyVersion({KEY_VERSION_ID: siteEncryptionKey?.keyVersionId.substring(0, 16) + '...'})}
        </div>
      </Tooltip>}
      <div className={siteEncryptionKeyMetadataEntry}>
        {siteEncryptionKey?.keyCreatedDate ? Messages.extractEncryptionTOLKeyCreatedDate({KEY_CREATION_DATE: siteEncryptionKey?.keyCreatedDate.substring(0, 10)}): null}
      </div>
      <div className={siteEncryptionKeyMetadataEntry}>
        {siteEncryptionKey?.keyRotatedDate ? Messages.extractEncryptionTOLKeyRotatedDate({KEY_ROTATED_DATE: siteEncryptionKey?.keyRotatedDate.substring(0,10)}): null}
      </div>
    </div>;
  } else {
    return null;
  }
}

/**
 * Display KmsInOnline settings only if:
 * 1. Instance is in TOL
 * 2. The user is a site admin
 * 3. The feature is enabled for a given site
 * TODO 4. The site is licensed to use the feature by Zeus
 */
function useShowSettingsSection(props: KMSInOnlineProps): boolean {
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteAdmin = useSelector(SessionSiteSelectors.isSessionSiteAdmin);
  const isCmekAvailable = props.settings.cmekAvailable;

  const isAllowedToChangeSetting = isOnlineEnvironment && isSiteAdmin;
  return isAllowedToChangeSetting && (FeatureFlags.cmekAvailableForSite || isCmekAvailable) && !FeatureFlags.enableExternalKeyManagementEnabled;
}

/* Register component in settings registry */
export const KMSInOnlineSpecs = componentRegistryEntry({
  name: 'KMSInOnlineSection',
  component: KMSInOnlineSection,
});
